import type { ReactNode } from 'react'
import { Button } from '@/components/Button'
import { LeaseTermButton } from '@/components/LeaseTermButton'
import { Tooltip } from '@/components/Tooltip'
import { useMediaQuery } from '@/hooks/useMediaQuery'

export interface OutboundLink {
	label: string
	href: string
}

interface PricingPanelProps {
	/** The lease bar renders only when both are given; omit them when pricing is hidden. */
	moveInDate?: string
	leaseTerm?: string
	totalPrice: string
	/** Rendered in place of the price when pricing is hidden (e.g. a contact message). */
	priceFallback?: ReactNode
	hasFees?: boolean
	baseRent?: string
	applicationFee?: string
	moveInCost?: string
	moveOutCost?: string
	onEditLease?: () => void
	onApply?: () => void
	showApply?: boolean
	primaryLabel?: string
	outboundLinks?: OutboundLink[]
	onCalculator?: () => void
	className?: string
}

export function PricingPanel ({
	moveInDate,
	leaseTerm,
	totalPrice,
	priceFallback,
	hasFees = true,
	baseRent,
	applicationFee,
	moveInCost,
	moveOutCost,
	onEditLease,
	onApply,
	showApply = true,
	primaryLabel = 'Apply',
	outboundLinks = [],
	onCalculator,
	className = ''
}: PricingPanelProps) {
	const isDesktop = useMediaQuery('(min-width: 768px)')

	const hasActions = showApply || outboundLinks.length > 0
	const hasLeaseBar = !!moveInDate && !!leaseTerm

	return (
		<div
			className={`spaces-pricing-panel spaces:bg-canvas-4 spaces:border spaces:border-canvas-3 spaces:rounded-16 spaces:overflow-hidden spaces:shadow-sm-below spaces:flex spaces:flex-col ${className}`}
		>
			{/* Lease options header */}
			{hasLeaseBar && (
				<LeaseTermButton
					moveInDate={moveInDate}
					leaseTerm={leaseTerm}
					onEdit={onEditLease}
				/>
			)}

			{/* Summary — without the lease bar the panel's own border and radius frame it */}
			<div className={`spaces:bg-canvas-base spaces:border-t spaces:flex spaces:flex-col spaces:gap-24 spaces:p-24 ${hasLeaseBar ? 'spaces:border-canvas-3 spaces:rounded-t-16' : 'spaces:border-transparent'}`}>
				{/* Pricing content */}
				{!totalPrice && priceFallback}
				{totalPrice && (hasFees ? (
					<div className='spaces:flex spaces:flex-col spaces:gap-12'>
						<div className='spaces:flex spaces:flex-col'>
							<span className='spaces:text-14/16 spaces:font-default spaces:text-content-3'>
								Total Monthly Leasing Price
							</span>
							<div
								className={`spaces:flex ${
									isDesktop ? 'spaces:items-baseline spaces:justify-between' : 'spaces:flex-col'
								}`}
							>
								{/* Total price wrapper — inline flow so suffix sticks to last line of price */}
								<div>
									<span className='spaces:text-24/32 spaces:font-strong spaces:text-content-1'>
										{totalPrice}
									</span>
									<span className='spaces:text-16/24 spaces:font-default spaces:text-content-3'>/mo</span>
									<span className='spaces:text-16/24 spaces:font-default spaces:text-content-3'>*</span>
									{onCalculator && (
										<span className='spaces:inline-flex spaces:align-middle'>
											<Tooltip label='Calculate My Cost' placement='top'>
												<Button
													iconOnly='Calculator'
													appearance='ghost'
													color='content4'
													size='md'
													aria-label='Calculate My Cost'
													onClick={onCalculator}
												/>
											</Tooltip>
										</span>
									)}
								</div>
								{baseRent && (
									<span className='spaces:text-14/16 spaces:font-default spaces:text-content-3 spaces:whitespace-nowrap'>
										{baseRent} Base Rent
									</span>
								)}
							</div>
						</div>

						{/* Divider */}
						<div className='spaces:h-px spaces:bg-canvas-3' />

						{/* Additional costs */}
						{(() => {
							const costItems = [
								applicationFee && { amount: applicationFee, label: 'at application' },
								moveInCost && { amount: moveInCost, label: 'at move-in' },
								moveOutCost && { amount: moveOutCost, label: 'at move-out' }
							].filter(Boolean) as { amount: string; label: string }[]

							return costItems.length > 0 ? (
								<div
									className={`spaces:flex ${
										isDesktop ? 'spaces:flex-wrap spaces:items-center' : 'spaces:flex-col'
									} spaces:gap-8 spaces:pt-4 spaces:text-14/16`}
								>
									{costItems.map((item, index) => (
										<div key={item.label} className='spaces:flex spaces:items-center spaces:gap-8'>
											{isDesktop && index > 0 && <div className='spaces:w-px spaces:h-16 spaces:bg-canvas-3' />}
											<div className='spaces:flex spaces:items-center spaces:gap-4'>
												<span className='spaces:font-strong spaces:text-content-1'>{item.amount}</span>
												<span className='spaces:font-default spaces:text-content-3'>{item.label}</span>
											</div>
										</div>
									))}
								</div>
							) : null
						})()}
					</div>
				) : (
					<div className='spaces:flex spaces:items-baseline spaces:gap-8'>
						<span className='spaces:text-24/32 spaces:font-strong spaces:text-content-1'>
							{totalPrice}
						</span>
						<span className='spaces:text-16/24 spaces:font-default spaces:text-content-3'>Base Rent*</span>
					</div>
				))}

				{/* Actions */}
				{hasActions && (
					<div className='spaces:flex spaces:flex-col spaces:gap-8'>
						{/* Primary row */}
						<div className={`spaces:flex ${isDesktop ? 'spaces:flex-row' : 'spaces:flex-col'} spaces:gap-8`}>
							{showApply && (
								<Button
									label={primaryLabel}
									appearance='solid'
									color='brand'
									size='lg'
									spacesClass='spaces-apply'
									onClick={onApply}
									className='spaces:flex-1'
								/>
							)}
							{outboundLinks.length > 0 && (
								<Button
									label={outboundLinks[0].label}
									appearance='stroke'
									color='brand'
									size='lg'
									href={outboundLinks[0].href}
									target='_blank'
									spacesClass='spaces-outbound-link'
									className='spaces:flex-1'
								/>
							)}
						</div>
						{/* Secondary row */}
						{outboundLinks.length > 1 && (
							<div className='spaces:flex spaces:flex-wrap spaces:gap-8'>
								{outboundLinks.slice(1).map(link => (
									<Button
										key={link.href}
										label={link.label}
										appearance='stroke'
										color='content2'
										size='sm'
										href={link.href}
										target='_blank'
									/>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}
